import type { AdminConfigValueType } from '@carcheck/shared';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Skeleton from '@mui/material/Skeleton';
import Typography from '@mui/material/Typography';
import { observer } from 'mobx-react-lite';
import { useMemo, useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { ConfigFilterBar } from './_components/ConfigFilterBar';
import { ConfigTable } from './_components/ConfigTable';
import { describeConfigKey } from './_lib/configDisplay';
import {
  distinctGroups,
  filterConfigItems,
  hasActiveFilters,
  EMPTY_CONFIG_FILTERS,
  type ConfigFilters,
} from './_lib/configRows';
import { useSetConfig } from './_lib/useSetConfig';
import { useAdminConfigList } from '../../api/admin-config';
import { RequirePermission } from '../../auth';

/**
 * `/config` — admin runtime-config table (AUT-131 / ADMIN-19), the 7th
 * admin vertical. Consumes the AUT-120 READ (`GET /admin/config` +
 * `/:key/history`) + the AUT-121 step-up-gated `PATCH /admin/config/:key`.
 *
 * Renders the 44 REAL registry keys (NOT the mockup's fictional ones),
 * grouped by domain prefix, with type-specific inline editors + lazy
 * per-key history. Search + type/group filters are CLIENT-side over the
 * bounded set (architect guardrail #7 — no server params, no cursor).
 *
 * Each PATCH (`useSetConfig`) is role + permission + audit gated
 * server-side (AUT-138 removed per-action TOTP step-up).
 *
 * Gated on `manage_settings` (matches the GET list/history permission +
 * the PATCH permission).
 */
const ConfigPageInner = (): ReactElement => {
  const { t } = useTranslation();
  const query = useAdminConfigList();
  const [filters, setFilters] = useState<ConfigFilters>(EMPTY_CONFIG_FILTERS);
  const { busy, run } = useSetConfig();

  const items = useMemo(() => query.data?.items ?? [], [query.data]);
  const groups = useMemo(() => distinctGroups(items), [items]);
  // AUT-284 — search matches what the operator can SEE (the human label),
  // not only the key the row no longer shows.
  const labelOf = useMemo(() => (key: string) => describeConfigKey(key, t).label, [t]);
  const filtered = useMemo(
    () => filterConfigItems(items, filters, labelOf),
    [items, filters, labelOf],
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      <Box>
        <Typography variant="h1" sx={{ fontSize: 22, fontWeight: 700 }}>
          {t('config.title')}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {t('config.subtitle', { count: items.length })}
        </Typography>
      </Box>

      <ConfigFilterBar
        filters={filters}
        groups={groups}
        onSearchChange={(search) => setFilters((f) => ({ ...f, search }))}
        onTypeChange={(type: AdminConfigValueType | null) => setFilters((f) => ({ ...f, type }))}
        onGroupChange={(group) => setFilters((f) => ({ ...f, group }))}
        canClear={hasActiveFilters(filters)}
        onClear={() => setFilters(EMPTY_CONFIG_FILTERS)}
      />

      {query.error ? (
        <Alert severity="error" role="alert">
          {t('config.loadError')}
        </Alert>
      ) : query.isLoading ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {[0, 1, 2, 3, 4].map((i) => (
            <Skeleton key={i} variant="rounded" height={44} />
          ))}
        </Box>
      ) : (
        <ConfigTable
          items={filtered}
          onSave={run}
          busy={busy}
          empty={
            <Typography variant="body2" color="text.secondary">
              {t('config.empty')}
            </Typography>
          }
        />
      )}
    </Box>
  );
};

const Observed = observer(ConfigPageInner);

/** Whole-page gate: no `manage_settings` → the default 403 panel. */
export const ConfigPage = (): ReactElement => (
  <RequirePermission perm="manage_settings">
    <Observed />
  </RequirePermission>
);
